import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import classNames from "classnames";
import { v4 as uuidv4 } from "uuid";
import { Button, BUTTON_SIZE, BUTTON_THEME } from "App/core/Button";
import {
  routePaths,
  SHARE_KEY_PREVIEW_PARAM,
  TRACKSTORY_AUTO_START_PARAM,
  TRACKSTORY_QUERY_PARAM,
} from "App/modules/urls";
import { useGenerateCommentsContext } from "./GenerateCommentsContext";
import { useAuth } from "App/components/Auth";
import { useFiles } from "App/components/useFiles";
import {
  stripExtension,
  isFilePlayable,
} from "@highnote/server/src/core/shared-util";
import {
  highnote,
  CLIENT_CELEBRITY_DEMO_USER_SHORTNAMES,
} from "@highnote/server/src/sdk";
import { PERSONAS } from "./personas";
import {
  AUDIO_ACCEPT_TYPES,
  COLLECTION_ID,
  EVENT_ID,
  FileEntity,
  ROLE,
  SHARE_KEY_PREFIX,
} from "@highnote/server/src/core/entities";
import { useSegmentContext } from "App/modules/useSegment";
import { PromptCard } from "./PromptCard";
import { About } from "./About";
import { ReactComponent as LightningSVG } from "App/common/icons/lightning.svg";
import { ReactComponent as FileSVG } from "App/common/icons-v2/file-music-line.svg";
import { ReactComponent as LogoFullSVG } from "App/common/icons-v2/logo-full.svg";
import LoadingScreen from "App/common/LoadingScreen/LoadingScreen";
import { useToast } from "App/common/useToast";
import { useMobileAppParams } from "../Main/useMobileAppParams";
import "./GenerateComments.scss";

// Display order for persona cards
const PERSONAS_ORDER = [
  PERSONAS[CLIENT_CELEBRITY_DEMO_USER_SHORTNAMES.DAUBREY],
  PERSONAS[CLIENT_CELEBRITY_DEMO_USER_SHORTNAMES.LAYNE_ZO],
  PERSONAS[CLIENT_CELEBRITY_DEMO_USER_SHORTNAMES.NOPRAH],
  PERSONAS[CLIENT_CELEBRITY_DEMO_USER_SHORTNAMES.LENDRICK],
  PERSONAS[CLIENT_CELEBRITY_DEMO_USER_SHORTNAMES.NUPAUL],
  PERSONAS[CLIENT_CELEBRITY_DEMO_USER_SHORTNAMES.GIFS],
  PERSONAS.KAROKEBOT,
  PERSONAS.BLANK,
];

const LOADING_STATUS = {
  UPLOADING: "Uploading audio...",
  PROCESSING: "Processing audio...",
  CREATING_TRACK: "Creating track page...",
  CREATING_SPACE: "Creating page...",
  CONFIGURING_SPACE: "Making page sharable...",
  TRANSCRIBING: "Isolating vocals and transcribing...",
  REDIRECTING: "Getting page link...",
};

const ESTIMATED_LOAD_TIME_SECONDS = 90;

export const GenerateComments = () => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const {
    selectedFile,
    setSelectedFile,
    selectedPersona,
    setSelectedPersona,
    commentGenSessionId,
  } = useGenerateCommentsContext();
  const { uploadFiles } = useFiles();
  const { addErrorMessage } = useToast();
  const { redirectToMobileApp } = useMobileAppParams();
  const { trackEvent } = useSegmentContext();
  const fromMobileApp =
    new URLSearchParams(location.search).get("fromMobileApp") === "true";
  const baseEventProperties = {
    commentGenSessionId,
    fromMobileApp,
  };
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [loadingText, setLoadingText] = useState<string>("");
  const [countdown, setCountdown] = useState<number>(60);
  const dragDepthRef = React.useRef(0);

  // Track page entry
  useEffect(() => {
    const eventId = user
      ? EVENT_ID.COMMENT_GEN_VIEWED
      : EVENT_ID.COMMENT_GEN_VIEWED_ANON;
    trackEvent(eventId, baseEventProperties);
  }, []);

  // Countdown timer for loading state
  useEffect(() => {
    if (isLoading && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
    // Reset countdown when loading stops
    if (!isLoading) {
      setCountdown(ESTIMATED_LOAD_TIME_SECONDS);
    }
  }, [isLoading, countdown]);

  // Auto-start upload if returning from auth with autoStart query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (
      params.get(TRACKSTORY_AUTO_START_PARAM) === "true" &&
      user &&
      selectedFile &&
      !isLoading
    ) {
      trackEvent(EVENT_ID.COMMENT_GEN_RETURNED_FROM_AUTH, baseEventProperties);
      // Remove the query parameter
      history.replace(routePaths.trackstories);
      // Start upload
      handleGenerate();
    }
  }, [location.search, user, selectedFile, isLoading]);

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    trackEvent(EVENT_ID.COMMENT_GEN_POPULATED_FILE, {
      ...baseEventProperties,
      fileType: file.type,
      fileSize: file.size,
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelected(file);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Check if it's a valid audio file type
      if (AUDIO_ACCEPT_TYPES.includes(file.type)) {
        handleFileSelected(file);
      }
    }
  };

  const waitForCommentGeneration = async (
    versionFileId: string,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      let generationInterval: ReturnType<typeof setInterval> | null = null;

      const personaData = Object.values(PERSONAS).find(
        (persona) => persona.id === selectedPersona,
      );
      const statusSlug = personaData?.statusSlug || "comments";
      const promptStatus = `Generating ${statusSlug}...`;
      setLoadingText(promptStatus);
      console.log("Starting comment generation...");

      // Start comment generation without awaiting
      highnote
        .generateComments({
          versionFileId,
          persona: selectedPersona,
        })
        .catch((error) => {
          console.log("Error starting comment generation:", error);
          if (generationInterval) {
            clearInterval(generationInterval);
          }
          reject("Error starting comment generation");
        });

      // Watch for comment generation completion
      const unsubscribe = highnote.watchEntity<FileEntity>({
        entityId: versionFileId,
        collectionId: COLLECTION_ID.FILE,
        onChange: (updatedFile) => {
          if (updatedFile.aiCommentGenerationError) {
            console.error(
              "Comment generation error:",
              updatedFile.aiCommentGenerationError,
            );
            if (generationInterval) {
              clearInterval(generationInterval);
            }
            unsubscribe();
            reject("Error generating comments");
            return;
          }

          if (updatedFile.aiCommentsGenerated) {
            console.log("Comment generation complete");
            if (generationInterval) {
              clearInterval(generationInterval);
            }
            unsubscribe();
            resolve();
          } else if (!generationInterval) {
            generationInterval = setInterval(() => {
              console.log("Generating comments...");
            }, 1000);
          }
        },
        onError: (error) => {
          console.error("Error watching file for comment generation:", error);
          if (generationInterval) {
            clearInterval(generationInterval);
          }
          reject(error);
        },
      });
    });
  };

  const waitForTranscriptionProcessing = async (
    versionFileId: string,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      let transcriptionInterval: ReturnType<typeof setInterval> | null = null;
      setLoadingText(LOADING_STATUS.TRANSCRIBING);
      console.log("Starting transcription...");

      // Start transcription without awaiting
      highnote.transcribeAudio({ versionFileId }).catch((error) => {
        console.log("Error starting transcription:", error);
        if (transcriptionInterval) {
          clearInterval(transcriptionInterval);
        }
        reject("Error starting transcription");
      });

      // Watch for transcription completion
      const unsubscribe = highnote.watchEntity<FileEntity>({
        entityId: versionFileId,
        collectionId: COLLECTION_ID.FILE,
        onChange: (updatedFile) => {
          if (updatedFile.transcriptionError) {
            console.error(
              "Transcription error:",
              updatedFile.transcriptionError,
            );
            if (transcriptionInterval) {
              clearInterval(transcriptionInterval);
            }
            unsubscribe();
            reject("Error transcribing file");
            return;
          }

          if (updatedFile.isTranscribed) {
            console.log("Transcription complete");
            if (transcriptionInterval) {
              clearInterval(transcriptionInterval);
            }
            unsubscribe();
            resolve();
          } else if (!transcriptionInterval) {
            transcriptionInterval = setInterval(() => {
              console.log("Transcribing audio...");
            }, 1000);
          }
        },
        onError: (error) => {
          console.error("Error watching file for transcription:", error);
          if (transcriptionInterval) {
            clearInterval(transcriptionInterval);
          }
          reject(error);
        },
      });
    });
  };

  const waitForAudioProcessing = async (
    fileEntityId: string,
  ): Promise<FileEntity> => {
    return new Promise((resolve, reject) => {
      let processingInterval: ReturnType<typeof setInterval> | null = null;
      setLoadingText(LOADING_STATUS.PROCESSING);
      const unsubscribe = highnote.watchEntity<FileEntity>({
        entityId: fileEntityId,
        collectionId: COLLECTION_ID.FILE,
        onChange: async (updatedFile) => {
          if (updatedFile.processingErrorV3) {
            console.error(
              "File processing error:",
              updatedFile.processingErrorV3,
            );
            if (processingInterval) {
              clearInterval(processingInterval);
            }
            unsubscribe();
            console.log(
              "Error processing file:",
              updatedFile.processingErrorV3,
            );
            reject("Error processing audio");
            return;
          }
          if (isFilePlayable(updatedFile)) {
            console.log("Conversion complete");
            if (processingInterval) {
              clearInterval(processingInterval);
            }
            unsubscribe();
            resolve(updatedFile);
          } else if (!processingInterval) {
            processingInterval = setInterval(() => {
              console.log("Processing and converting audio...");
            }, 1000);
          }
        },
        onError: (error) => {
          console.error("Error watching file:", error);
          if (processingInterval) {
            clearInterval(processingInterval);
          }
          reject("Error processing audio");
        },
      });
    });
  };

  const createEntities = async (
    fileEntity: FileEntity,
  ): Promise<{ trackId: string; spaceId: string }> => {
    try {
      const trackId = uuidv4();
      const spaceId = uuidv4();
      const shareKey = `${SHARE_KEY_PREFIX}${uuidv4()}`;
      setLoadingText(LOADING_STATUS.CREATING_SPACE);
      await highnote.createSpace({
        id: spaceId,
        data: {
          name: stripExtension(fileEntity.fileName),
        },
      });
      setLoadingText(LOADING_STATUS.CONFIGURING_SPACE);
      await highnote.updateSpace({
        id: spaceId,
        data: {
          name: stripExtension(fileEntity.fileName),
          [`rolesV2,${shareKey}`]: [
            ROLE.VIEW,
            ROLE.DOWNLOAD,
            ROLE.GUESTS_ALLOWED,
          ],
        },
      });
      setLoadingText(LOADING_STATUS.CREATING_TRACK);
      await highnote.createTrack({
        id: trackId,
        data: {
          title: stripExtension(fileEntity.fileName),
          spaceId,
          versionFilesV2: [fileEntity.id],
          defaultVersionId: fileEntity.id,
        },
      });
      return { trackId, spaceId };
    } catch (error) {
      console.error("Error creating entities:", error);
      throw error;
    }
  };

  const redirectToTrackPage = (
    trackId: string,
    spaceId: string,
    skipQueryParams = false,
  ) => {
    setLoadingText(LOADING_STATUS.REDIRECTING);
    const trackUrl = routePaths.spaceTrack
      .replace(":spaceId", spaceId)
      .replace(":trackId", trackId);

    trackEvent(EVENT_ID.COMMENT_GEN_REDIRECTING_TO_TRACK, {
      ...baseEventProperties,
      trackId,
      spaceId,
    });

    // If coming from mobile app, redirect back to the native app
    if (fromMobileApp) {
      // Strip leading slash since redirectToMobileApp adds one after the scheme
      redirectToMobileApp(trackUrl.slice(1));
      return;
    }

    if (skipQueryParams) {
      history.push(trackUrl);
    } else {
      const urlWithParams = `${trackUrl}?${TRACKSTORY_QUERY_PARAM}=true&${SHARE_KEY_PREVIEW_PARAM}=true`;
      history.push(urlWithParams);
    }
  };

  const uploadTrack = async (): Promise<FileEntity> => {
    return new Promise((resolve, reject) => {
      if (!selectedFile) return reject("No file selected");
      setLoadingText(LOADING_STATUS.UPLOADING);
      uploadFiles({
        payloads: [
          {
            file: selectedFile,
            onStateChange: (snapshot) => {
              const progress = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
              );
              setLoadingText(LOADING_STATUS.UPLOADING);
              console.log(`Uploading audio: ${progress}%`);
            },
          },
        ],
        onSuccess: async (_, fileEntity) => {
          resolve(fileEntity);
        },
        onError: (e) => {
          console.error("Error uploading file:", e);
          reject("Error uploading file");
        },
      });
    });
  };

  const handleGenerate = async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    // If user is authenticated, upload and generate
    if (user) {
      try {
        const fileEntity = await uploadTrack();
        trackEvent(EVENT_ID.COMMENT_GEN_UPLOADED_TRACK, baseEventProperties);

        const { trackId, spaceId } = await createEntities(fileEntity);
        trackEvent(EVENT_ID.COMMENT_GEN_CREATED_ENTITIES, {
          ...baseEventProperties,
          trackId,
          spaceId,
        });

        const processedFile = await waitForAudioProcessing(fileEntity.id);
        trackEvent(EVENT_ID.COMMENT_GEN_PROCESSED_AUDIO, {
          ...baseEventProperties,
          durationSeconds: processedFile.metadata?.duration,
        });

        // Skip transcription and comment generation for BLANK
        if (selectedPersona !== PERSONAS.BLANK.id) {
          await waitForTranscriptionProcessing(fileEntity.id);
          trackEvent(
            EVENT_ID.COMMENT_GEN_TRANSCRIBED_AUDIO,
            baseEventProperties,
          );

          await waitForCommentGeneration(fileEntity.id);
          trackEvent(EVENT_ID.COMMENT_GEN_GENERATED_COMMENTS, {
            ...baseEventProperties,
            persona: selectedPersona,
          });
        }

        setIsLoading(false);
        setSelectedFile(null);
        redirectToTrackPage(
          trackId,
          spaceId,
          selectedPersona === PERSONAS.BLANK.id,
        );
      } catch (error) {
        console.error("Error generating comments:", error);
        addErrorMessage(error);
        setIsLoading(false);
        setSelectedFile(null);
      }
    } else {
      // Redirect to signup with returnTo parameter including autoStart flag
      trackEvent(EVENT_ID.COMMENT_GEN_REDIRECTING_TO_AUTH, baseEventProperties);
      const returnToUrl = `${routePaths.trackstories}?${TRACKSTORY_AUTO_START_PARAM}=true`;
      history.push(
        `${routePaths.signup}?${TRACKSTORY_AUTO_START_PARAM}=true&returnTo=${encodeURIComponent(returnToUrl)}`,
      );
    }
  };

  return (
    <div
      className={classNames("generate-comments", {
        "generate-comments--mobileApp": fromMobileApp,
      })}
    >
      {isLoading ? (
        <div className="generate-comments__loading-container">
          <div className="generate-comments__loadingScreen-wrapper">
            <LoadingScreen />
          </div>
          {loadingText && (
            <div className="generate-comments__loading-text">
              <div className="generate-comments__loading-header">
                {countdown > 0 ? loadingText : "Still working..."}
              </div>
              <div className="generate-comments__loading-subText">
                {countdown > 0
                  ? `~${countdown}s Remaining. Keep ${fromMobileApp ? "this" : "tab"} open.`
                  : "Audio length and connection speed affect wait time."}
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className={classNames("generate-comments__content", {
              "generate-comments__content--mobileApp": fromMobileApp,
            })}
          >
            {showAbout ? (
              <About onBack={() => setShowAbout(false)} />
            ) : (
              <>
                {/* Logo/Header */}
                <div
                  className={classNames("generate-comments__header", {
                    "generate-comments__header--mobileApp": fromMobileApp,
                  })}
                >
                  {fromMobileApp ? (
                    <h1 className="generate-comments__mobile-title">
                      Upload a Track
                    </h1>
                  ) : (
                    <LogoFullSVG className="generate-comments__logo" />
                  )}
                  <div className="generate-comments__description">
                    Highnote is where music talks. Upload a track to explore
                    annotations from a demo character, then start fresh with
                    your own annotations for fans to discover.
                  </div>
                </div>

                <div className="generate-comments__actions">
                  {/* File Input */}
                  <div className="generate-comments__input-requirements">
                    For best results, upload a ≤5 min MP3 with lyrics or speech.
                  </div>
                  <div className="generate-comments__input-wrapper">
                    <div
                      className={classNames(
                        "generate-comments__file-input-container",
                        {
                          "generate-comments__file-input-container--has-file":
                            selectedFile,
                          "generate-comments__file-input-container--dragging":
                            isDragging,
                        },
                      )}
                    >
                      <input
                        type="file"
                        accept={AUDIO_ACCEPT_TYPES.join(",")}
                        onChange={handleFileChange}
                        className="generate-comments__file-input"
                        id="audio-file-input"
                      />
                      <label
                        htmlFor="audio-file-input"
                        className={classNames("generate-comments__file-label", {
                          "generate-comments__file-label--has-file":
                            selectedFile,
                          "generate-comments__file-label--dragging": isDragging,
                        })}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      >
                        <FileSVG className="generate-comments__file-icon" />
                        <span className="generate-comments__file-text">
                          {selectedFile ? (
                            selectedFile.name
                          ) : (
                            <>
                              Select{" "}
                              <span className="generate-comments__file-text-desktop">
                                or drop{" "}
                              </span>
                              an audio file
                            </>
                          )}
                        </span>
                      </label>
                      <Button
                        theme={BUTTON_THEME.CTA}
                        size={BUTTON_SIZE.MEDIUM}
                        onClick={handleGenerate}
                        disabled={!selectedFile}
                        className="generate-comments__go-button"
                        endIcon={<LightningSVG />}
                      >
                        Go
                      </Button>
                    </div>
                  </div>

                  {/* Prompt Cards */}
                  <div className="generate-comments__prompts">
                    <div className="generate-comments__prompts-scroll">
                      {PERSONAS_ORDER.map((persona) => (
                        <PromptCard
                          key={persona.id}
                          option={persona}
                          selected={selectedPersona === persona.id}
                          onClick={() => {
                            setSelectedPersona(persona.id);
                            trackEvent(EVENT_ID.COMMENT_GEN_SELECTED_PERSONA, {
                              ...baseEventProperties,
                              persona: persona.id,
                            });
                          }}
                        />
                      ))}
                    </div>
                    <div className="generate-comments__prompts-fade" />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="generate-comments__footer">
            <button
              onClick={() => setShowAbout(true)}
              className={classNames("generate-comments__footer-link", {
                "generate-comments__footer-link--mobileApp": fromMobileApp,
              })}
            >
              About
            </button>
            <p
              className={classNames("generate-comments__footer-copyright", {
                "generate-comments__footer-copyright--mobileApp": fromMobileApp,
              })}
            >
              © 2026 Highnote, Inc
            </p>
          </div>
        </>
      )}
    </div>
  );
};
